import { useState } from 'react';
import {
  Callout,
  Chip,
  DateInput,
  Empty,
  Field,
  Loading,
  Panel,
  TableScroll,
  formatDate,
  useLoad,
  localDayStartOrNow,
  todayIso,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../../client.ts';
import { t } from '../../../messages.ts';
import { useAbilities } from '../../abilities.tsx';
import { DirectEditDialog } from '../../DirectEditDialog.tsx';
import {
  campaignActions,
  dealActions,
  dealEnded,
  type CampaignAction,
  type DealAction,
} from '../../direct-edit-model.ts';
import { LoginAsPickerButton } from '../../login-as/LoginAs.tsx';
import { Failure } from '../../shared.tsx';

type Campaign = Awaited<ReturnType<PlatformApi['operatorCampaigns']>>['items'][number];
type Deal = Awaited<ReturnType<PlatformApi['operatorDeals']>>['data'][number];

/**
 * One operator's campaigns and deals, and the switches a direct edit (3.9d)
 * may flip on them: pause, resume or retire a campaign; end a deal or
 * re-offer an ended one. Terms themselves are not edited here — a deal's
 * terms are a proposal the affiliate answers, built on the operator's own
 * forms — so the panel says to use Log in as for that.
 *
 * Loaded on an explicit click, as the other cross-tenant sections are: each
 * read is logged per access (3.9c).
 */
export function OperatorCampaignsDealsPanel({
  api,
  operatorId,
  operatorName,
  shown,
  onShow,
}: {
  api: PlatformApi;
  operatorId: string;
  operatorName: string;
  shown: boolean;
  onShow: () => void;
}) {
  const { can } = useAbilities();
  const [version, setVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [campaignAct, setCampaignAct] = useState<{ row: Campaign; act: CampaignAction } | null>(null);
  const [dealAct, setDealAct] = useState<{ row: Deal; act: DealAction } | null>(null);
  const campaigns = useLoad(
    () => (shown ? api.operatorCampaigns(operatorId) : Promise.resolve(null)),
    [operatorId, shown, version],
  );
  const deals = useLoad(
    () => (shown ? api.operatorDeals(operatorId) : Promise.resolve(null)),
    [operatorId, shown, version],
  );
  const now = new Date();

  if (!shown) {
    return (
      <Panel title={t('edit.cd.title')}>
        <Callout>{t('edit.cd.note')}</Callout>
        <div className="form-actions">
          <button type="button" className="primary" onClick={onShow}>{t('edit.cd.view')}</button>
        </div>
      </Panel>
    );
  }

  const done = (note: string) => {
    setCampaignAct(null);
    setDealAct(null);
    setNotice(note);
    setVersion((v) => v + 1);
  };

  return (
    <>
      {notice ? <Callout fold={false}>{notice}</Callout> : null}

      <Panel
        title={t('edit.cd.campaigns')}
        aside={campaigns.state === 'ready' && campaigns.value ? (
          <span className="count count-badge">{campaigns.value.items.length}</span>
        ) : null}
      >
        {campaigns.state === 'error' ? <Failure error={campaigns.error} /> : null}
        {campaigns.state === 'loading' || (campaigns.state === 'ready' && !campaigns.value) ? <Loading rows={2} /> : null}
        {campaigns.state === 'ready' && campaigns.value ? (
          campaigns.value.items.length === 0 ? (
            <Empty>{t('edit.cd.noCampaigns')}</Empty>
          ) : (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <th>{t('edit.cd.colCampaign')}</th>
                    <th>{t('common.brand')}</th>
                    <th>{t('common.state')}</th>
                    <th className="num">{t('edit.cd.colUpdated')}</th>
                    {can.edit ? <th className="col-action" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {campaigns.value.items.map((c) => (
                    <tr key={c.campaign_id}>
                      <td>{c.name} <span className="faint mono">{c.code}</span></td>
                      <td className="faint">{c.brand_name}</td>
                      <td><Chip tone={campaignTone(c.state)}>{campaignStateLabel(c.state)}</Chip></td>
                      <td className="num faint">{formatDate(c.updated_at)}</td>
                      {can.edit ? (
                        <td className="num col-action">
                          <div className="row" style={{ justifyContent: 'flex-end' }}>
                            {campaignActions(c.state, can.edit).map((act) => (
                              <button
                                key={act}
                                type="button"
                                className={act === 'retire' ? 'danger-quiet' : 'copy'}
                                aria-label={`${campaignActLabel(act)} ${c.name}`}
                                onClick={() => { setNotice(null); setCampaignAct({ row: c, act }); }}
                              >
                                {campaignActLabel(act)}
                              </button>
                            ))}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )
        ) : null}
      </Panel>

      <Panel
        title={t('edit.cd.deals')}
        aside={deals.state === 'ready' && deals.value ? (
          <span className="count count-badge">{deals.value.data.length}</span>
        ) : null}
      >
        {deals.state === 'error' ? <Failure error={deals.error} /> : null}
        {deals.state === 'loading' || (deals.state === 'ready' && !deals.value) ? <Loading rows={2} /> : null}
        {deals.state === 'ready' && deals.value ? (
          deals.value.data.length === 0 ? (
            <Empty>{t('edit.cd.noDeals')}</Empty>
          ) : (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <th>{t('edit.cd.colDeal')}</th>
                    <th>{t('common.affiliate')}</th>
                    <th>{t('common.brand')}</th>
                    <th>{t('edit.cd.colTerms')}</th>
                    <th>{t('common.state')}</th>
                    {can.edit ? <th className="col-action" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {deals.value.data.map((d) => (
                    <tr key={d.deal_id}>
                      <td>{d.name || d.code}{d.name ? <span className="faint mono"> {d.code}</span> : null}</td>
                      <td>{d.affiliate ?? t('edit.cd.everyAffiliate')}</td>
                      <td className="faint">{d.brand}</td>
                      <td className="faint">
                        {d.live_type ? `${d.live_type} · v${d.live_version}` : '—'}
                        {d.pending_version !== null ? <div>{t('edit.cd.offerWaiting', { version: d.pending_version })}</div> : null}
                      </td>
                      <td>{dealStateChip(d, now)}</td>
                      {can.edit ? (
                        <td className="num col-action">
                          {dealActions(d, now, can.edit).map((act) => (
                            <button
                              key={act}
                              type="button"
                              className={act === 'end' ? 'danger-quiet' : 'copy'}
                              aria-label={`${act === 'end' ? t('edit.cd.end') : t('edit.cd.reactivate')} ${d.name || d.code}`}
                              onClick={() => { setNotice(null); setDealAct({ row: d, act }); }}
                            >
                              {act === 'end' ? t('edit.cd.end') : t('edit.cd.reactivate')}
                            </button>
                          ))}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )
        ) : null}
      </Panel>

      <div className="row">
        <span className="faint">{t('edit.cd.termsThroughLogin')}</span>
        <LoginAsPickerButton api={api} operator={{ id: operatorId, name: operatorName }} />
      </div>

      {campaignAct ? (
        <DirectEditDialog
          title={t('edit.cd.campaignTitle', { act: campaignActLabel(campaignAct.act), name: campaignAct.row.name })}
          submitLabel={campaignActLabel(campaignAct.act)}
          danger={campaignAct.act !== 'resume'}
          ready
          run={(reason) => api.campaignAct(operatorId, campaignAct.row.campaign_id, campaignAct.act, reason)}
          onClose={() => setCampaignAct(null)}
          onDone={() => done(
            campaignAct.act === 'pause' ? t('edit.cd.pausedDone', { name: campaignAct.row.name })
              : campaignAct.act === 'resume' ? t('edit.cd.resumedDone', { name: campaignAct.row.name })
                : t('edit.cd.retiredDone', { name: campaignAct.row.name }),
          )}
        >
          <p>
            {campaignAct.act === 'pause' ? t('edit.cd.pauseNote')
              : campaignAct.act === 'resume' ? t('edit.cd.resumeNote')
                : t('edit.cd.retireNote')}
          </p>
        </DirectEditDialog>
      ) : null}

      {dealAct?.act === 'end' ? (
        <DirectEditDialog
          title={t('edit.cd.endTitle', { name: dealAct.row.name || dealAct.row.code })}
          submitLabel={t('edit.cd.end')}
          danger
          ready
          run={(reason) => api.endDeal(operatorId, dealAct.row.deal_id, reason)}
          onClose={() => setDealAct(null)}
          onDone={() => done(t('edit.cd.endDone', { name: dealAct.row.name || dealAct.row.code }))}
        >
          <p>{t('edit.cd.endNote')}</p>
        </DirectEditDialog>
      ) : null}

      {dealAct?.act === 'reactivate' ? (
        <ReactivateDeal
          api={api}
          operatorId={operatorId}
          deal={dealAct.row}
          today={todayIso(now)}
          onClose={() => setDealAct(null)}
          onDone={() => done(t('edit.cd.reactivateDone', { name: dealAct.row.name || dealAct.row.code }))}
        />
      ) : null}
    </>
  );
}

function ReactivateDeal({
  api, operatorId, deal, today, onClose, onDone,
}: {
  api: PlatformApi; operatorId: string; deal: Deal; today: string; onClose: () => void; onDone: () => void;
}) {
  const [validFrom, setValidFrom] = useState(today);
  return (
    <DirectEditDialog
      title={t('edit.cd.reactivateTitle', { name: deal.name || deal.code })}
      submitLabel={t('edit.cd.reactivate')}
      ready={validFrom !== '' && validFrom >= today}
      run={(reason) => api.reactivateDeal(operatorId, deal.deal_id, localDayStartOrNow(validFrom), reason)}
      onClose={onClose}
      onDone={onDone}
    >
      <p>{t('edit.cd.reactivateNote')}</p>
      <Field label={t('edit.cd.validFrom')} hint={t('edit.cd.validFromHint')}>
        <DateInput value={validFrom} onChange={setValidFrom} min={today} today={today} />
      </Field>
    </DirectEditDialog>
  );
}

function campaignActLabel(act: CampaignAction): string {
  return act === 'pause' ? t('edit.cd.pause') : act === 'resume' ? t('edit.cd.resume') : t('edit.cd.retire');
}

function campaignStateLabel(state: string | null): string {
  switch (state) {
    case 'ACTIVE': return t('edit.cd.stateActive');
    case 'PAUSED': return t('edit.cd.statePaused');
    case 'RETIRED': return t('edit.cd.stateRetired');
    case 'DRAFT': return t('edit.cd.stateDraft');
    default: return '—';
  }
}

function campaignTone(state: string | null): 'locked' | 'provisional' | 'neutral' | 'overdue' {
  return state === 'ACTIVE' ? 'locked' : state === 'PAUSED' ? 'provisional' : state === 'RETIRED' ? 'overdue' : 'neutral';
}

function dealStateChip(d: Deal, now: Date) {
  if (dealEnded(d, now)) {
    return <Chip tone="neutral">{t('edit.cd.ended', { date: formatDate(d.ended_at!) })}</Chip>;
  }
  if (d.ended_at || d.live_valid_to) {
    return <Chip tone="provisional">{t('edit.cd.ends', { date: formatDate((d.ended_at ?? d.live_valid_to)!) })}</Chip>;
  }
  if (d.live_version === null) {
    return <Chip tone="neutral">{t('edit.cd.notAgreed')}</Chip>;
  }
  return <Chip tone="locked">{t('edit.cd.running')}</Chip>;
}
